import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, MessageCircle, Instagram, Youtube } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { FAQS, MEDIA, CONTACT, WHATSAPP_URL, TEL_URL, SOCIAL, NAV_LINKS, SEO_LINKS } from "../../config/site";
import { Reveal, useEnquiry } from "../../lib/site-ui";

export const Faq = () => (
  <section id="faq" className="py-24 sm:py-32 lg:py-40 border-t border-white/10" data-testid="faq-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-8">
      <div className="mb-12">
        <Reveal as="p" className="label-mono text-slate-500 mb-4">07 — Questions</Reveal>
        <Reveal as="h2" className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
          Frequently Asked
        </Reveal>
      </div>
      <Reveal>
        <Accordion type="single" collapsible className="border-t border-white/10" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
              <AccordionTrigger className="text-left font-display text-lg sm:text-xl text-white hover:no-underline py-6" data-testid={`faq-trigger-${i}`}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-400 leading-relaxed text-base pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const Contact = () => {
  const { openEnquiry } = useEnquiry();
  return (
    <section id="contact" className="relative isolate overflow-hidden" data-testid="contact-section">
      <div className="absolute inset-0 -z-10">
        <img src={MEDIA.mumbai} alt="Mumbai skyline — AeroVaid DGCA ground classes in Mumbai" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/88" />
      </div>
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-24 sm:py-32 lg:py-40 text-center">
        <Reveal as="p" className="label-mono text-slate-400 mb-6">Mumbai, India</Reveal>
        <Reveal as="h2" className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white leading-[1.05]">
          Ready to Start Your DGCA Preparation?
        </Reveal>
        <Reveal className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
          Talk directly with AeroVaid and book your FREE demo class today.
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="contact-demo-btn"
              onClick={() => openEnquiry()}
              className="bg-white text-slate-950 font-semibold px-8 py-4 rounded-sm hover:bg-slate-200 transition-all hover:scale-[1.02] text-sm tracking-wide"
            >
              BOOK FREE DEMO
            </button>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn"
              className="flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold px-8 py-4 rounded-sm hover:bg-[#20bd5a] transition-colors text-sm tracking-wide">
              <MessageCircle className="w-4 h-4" /> WHATSAPP: {CONTACT.phoneDisplay}
            </a>
            <a href={TEL_URL} data-testid="contact-call-btn"
              className="flex items-center justify-center gap-2 border border-white/25 text-white px-8 py-4 rounded-sm hover:bg-white/5 transition-colors text-sm tracking-wide">
              <Phone className="w-4 h-4" /> CALL: {CONTACT.phoneDisplay}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { pathname } = useLocation();
  const to = (hash) => (pathname === "/" ? hash : `/${hash}`);
  return (
  <footer className="border-t border-white/10 bg-slate-950" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <h3 className="font-display text-2xl text-white tracking-tight">AeroVaid Aviation Academy</h3>
          <p className="mt-4 text-slate-400 leading-relaxed max-w-sm">
            DGCA Ground Coaching · Online Across India · Personalised Coaching in Mumbai
          </p>
          <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors">
            <MessageCircle className="w-4 h-4" /> {CONTACT.phoneDisplay}
          </a>
        </div>
        <div>
          <p className="label-mono text-slate-500 mb-5">Explore</p>
          <ul className="space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a href={to(l.href)} className="text-slate-400 hover:text-white transition-colors">{l.label}</a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="label-mono text-slate-500 mb-5">DGCA Classes</p>
          <ul className="space-y-3">
            <li>
              <Link to={SEO_LINKS.mumbai} className="text-slate-400 hover:text-white transition-colors" data-testid="footer-mumbai-link">
                DGCA ground classes in Mumbai
              </Link>
            </li>
            <li>
              <Link to={SEO_LINKS.online} className="text-slate-400 hover:text-white transition-colors" data-testid="footer-online-link">
                Online DGCA ground classes
              </Link>
            </li>
            <li>
              <a href={to("#courses")} className="text-slate-400 hover:text-white transition-colors">DGCA subjects</a>
            </li>
          </ul>
        </div>
        <div>
          <p className="label-mono text-slate-500 mb-5">Follow</p>
          <div className="flex gap-3">
            <a href={SOCIAL.instagram} target="_blank" rel="noreferrer" aria-label="AeroVaid on Instagram" data-testid="social-instagram"
              className="grid place-items-center w-11 h-11 border border-white/15 rounded-sm hover:bg-white/5 transition-colors">
              <Instagram className="w-5 h-5 text-white" />
            </a>
            <a href={SOCIAL.youtube} target="_blank" rel="noreferrer" aria-label="AeroVaid on YouTube" data-testid="social-youtube"
              className="grid place-items-center w-11 h-11 border border-white/15 rounded-sm hover:bg-white/5 transition-colors">
              <Youtube className="w-5 h-5 text-white" />
            </a>
          </div>
        </div>
      </div>
      <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-sm text-slate-500">
        <p>© {new Date().getFullYear()} AeroVaid Aviation Academy. All rights reserved.</p>
        <p>Mumbai, Maharashtra, India</p>
      </div>
    </div>
  </footer>
  );
};
